import { authFetch } from '@/lib/auth-fetch';
import { getApiBaseUrl } from '@/services/api';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Lifecycle status of onboarding for a given workspace/company.
 *
 * - notStarted  → no admin has opened the wizard yet; auto-show to the first admin
 * - inProgress  → an admin has started but not finished; other admins see "Resume" option
 * - completed   → wizard was completed; no more auto-show, sidebar option disabled
 */
export type OnboardingLifecycleStatus = 'notStarted' | 'inProgress' | 'completed';

export interface OnboardingStatus {
  instanceId: string;
  /** Granular lifecycle state (replaces the old boolean isCompleted). */
  status: OnboardingLifecycleStatus;
  /** Legacy boolean — kept for backwards compatibility with older backends. */
  isCompleted: boolean;
  completedAt: string | null;
  startedAt: string | null;
  /** Persisted step progress (0 = not started). */
  savedStep: number;
  /** Number of steps skipped. */
  skipCount: number;
}

/** Shape returned by the new persistence-backed controller. */
interface OnboardingStatusResponse {
  hasCompleted: boolean;
  savedStep: number;
  skipCount: number;
  completedAt?: string | null;
  stepStatuses?: Record<string, string> | null;
}

/**
 * Derives the lifecycle status from the persisted state.
 */
function deriveLifecycleStatus(data: OnboardingStatusResponse): OnboardingLifecycleStatus {
  if (data.hasCompleted) return 'completed';
  if (data.savedStep > 0 || data.skipCount > 0) return 'inProgress';
  return 'notStarted';
}

/**
 * Maps the persistence-layer response to the frontend OnboardingStatus shape.
 */
function mapToOnboardingStatus(data: OnboardingStatusResponse, instanceId: string): OnboardingStatus {
  const status = deriveLifecycleStatus(data);
  return {
    instanceId,
    status,
    isCompleted: data.hasCompleted,
    completedAt: data.completedAt ?? null,
    startedAt: data.savedStep > 0 ? data.completedAt ?? null : null,
    savedStep: data.savedStep,
    skipCount: data.skipCount,
  };
}

// ─── API calls ───────────────────────────────────────────────────────────────

/**
 * GET /api/onboarding/status
 * Returns null if the backend is unreachable (graceful degradation to localStorage).
 */
export async function fetchOnboardingStatus(
  instanceId: string,
): Promise<OnboardingStatus | null> {
  try {
    const base = getApiBaseUrl();
    const url = `${base}/api/onboarding/status`;
    const res = await authFetch(url, { credentials: 'include' });
    if (!res.ok) return null;
    const data: OnboardingStatusResponse = await res.json();
    return mapToOnboardingStatus(data, instanceId);
  } catch {
    // Backend unavailable — caller falls back to localStorage
    return null;
  }
}

/**
 * POST /api/onboarding/start
 * Marks onboarding as inProgress (idempotent — safe to call multiple times).
 * Returns null if the backend is unreachable.
 */
export async function postOnboardingStart(
  instanceId: string,
): Promise<OnboardingStatus | null> {
  try {
    const base = getApiBaseUrl();
    const url = `${base}/api/onboarding/status`;
    const res = await authFetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ savedStep: 1 }),
      credentials: 'include',
    });
    if (!res.ok) return null;
    // PUT returns 200 with no body; re-fetch to get latest state
    return await fetchOnboardingStatus(instanceId);
  } catch {
    return null;
  }
}

/**
 * POST /api/onboarding/complete
 * Returns null if the backend is unreachable.
 */
export async function postOnboardingComplete(
  instanceId: string,
): Promise<OnboardingStatus | null> {
  try {
    const base = getApiBaseUrl();
    const url = `${base}/api/onboarding/complete`;
    const res = await authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      credentials: 'include',
    });
    if (!res.ok) return null;
    // Return completed status
    return {
      instanceId,
      status: 'completed',
      isCompleted: true,
      completedAt: new Date().toISOString(),
      startedAt: null,
      savedStep: 0,
      skipCount: 0,
    };
  } catch {
    return null;
  }
}

/**
 * PUT /api/onboarding/status
 * Persists the current step and skip count to the server.
 * Returns null if the backend is unreachable.
 */
export async function putOnboardingStep(
  savedStep: number,
  skipCount: number,
): Promise<boolean> {
  try {
    const base = getApiBaseUrl();
    const url = `${base}/api/onboarding/status`;
    const res = await authFetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ savedStep, skipCount }),
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * POST /api/onboarding/reset
 * Returns null if the backend is unreachable.
 */
export async function postOnboardingReset(
  instanceId: string,
): Promise<OnboardingStatus | null> {
  try {
    const base = getApiBaseUrl();
    const url = `${base}/api/onboarding/reset`;
    const res = await authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      credentials: 'include',
    });
    if (!res.ok) return null;
    return {
      instanceId,
      status: 'notStarted',
      isCompleted: false,
      completedAt: null,
      startedAt: null,
      savedStep: 0,
      skipCount: 0,
    };
  } catch {
    return null;
  }
}
